import React, { useState, useEffect, useContext } from 'react';
import { useDebounce } from '@erecruit/core';
import {
  Select,
  Option,
  Spinner,
  SelectProps,
  FormContext,
  FormContextType,
} from '@erecruit/ui-kit';

import { PositionField } from '../../types';
import { fetchData } from './helpers';
import { getFieldSlug } from '../../helpers';

interface AsyncSelectProps extends SelectProps {
  field: PositionField;
  mode?: 'default' | 'multiple';
}

interface OptionProps {
  id: string;
  name: string;
}

const AsyncSelect = ({ field, mode = 'default' }: AsyncSelectProps) => {
  const label = getFieldSlug(field);
  const { setFieldsValue, getFieldsValue } = useContext<FormContextType>(
    FormContext,
  );

  const contextFieldValue = getFieldsValue([label]);

  const [missingDataLoaded, setMissingDataLoaded] = useState(false);
  const [searchDataLoaded, setSearchDataLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState<string>(contextFieldValue[label]);
  const [multiValue, setMultiValue] = useState<string[]>(
    contextFieldValue[label] ? contextFieldValue[label] : [],
  );
  const [searchTerm, setSearchTerm] = useState();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const initialData = (field.data || []) as OptionProps[];

  const [data, setData] = useState<OptionProps[]>(initialData);

  function handleOnSelect(selectedValue: string) {
    setLoading(false);
    if (mode === 'multiple') {
      const newValue = multiValue.concat([selectedValue]);
      setMultiValue(newValue);
      setFieldsValue({
        [label]: newValue,
      });
    } else {
      setValue(selectedValue);
      setFieldsValue({
        [label]: selectedValue,
      });
    }
  }

  function handleSearch(value: string) {
    setSearchDataLoaded(false);
    setSearchTerm(value);
  }

  useEffect(() => {
    if (debouncedSearchTerm && !loading && !searchDataLoaded) {
      setLoading(true);
      fetchData<OptionProps>(field, debouncedSearchTerm).then(data => {
        setData(data);
        setSearchDataLoaded(true);
        setLoading(false);
      });
    }
  }, [debouncedSearchTerm, field, initialData, loading, searchDataLoaded]);

  useEffect(() => {
    if (value && !missingDataLoaded) {
      const fieldValue = data.find(item => Number(item.id) === Number(value));
      setMissingDataLoaded(true);
      if (!fieldValue) {
        const missingIds = mode === 'multiple' ? field.value : [field.value];
        fetchData<OptionProps>(field, undefined, missingIds.map(Number)).then(
          missingData => {
            const newData = data.concat(
              missingData.map(datum => ({ ...datum, id: datum.id })),
            );
            setData(newData);
          },
        );
      }
    }
  }, [value, data, field, mode, missingDataLoaded]);

  useEffect(() => {
    if (contextFieldValue[label] && contextFieldValue[label] !== value) {
      setValue(contextFieldValue[label]);
      setMultiValue(contextFieldValue[label]);
    }
  }, [contextFieldValue, label, value]);

  return (
    <Select
      showSearch
      notFoundContent={
        loading ? <Spinner size="small" spinning={true} /> : null
      }
      mode={mode}
      filterOption={false}
      onSearch={handleSearch}
      onSelect={handleOnSelect}
      disabled={field.settings && field.settings.locked}
      initialValue={field.value}
      value={mode === 'multiple' ? multiValue : value}
    >
      {data.map(obj => (
        <Option key={obj.id} value={obj.id}>
          {obj.name}
        </Option>
      ))}
    </Select>
  );
};

export default AsyncSelect;
